/**
 * Meeting Note TipTap Document
 *
 * Canonical shape for the TipTap document that backs meeting notes: the node
 * and mark allowlist, a strict validator, a repair-oriented normalizer, and the
 * store-model converters.
 *
 * The converters (`sectionsToEnhancedDocument` / `documentToEnhancedSections`)
 * are consumed today by the electron app's meeting notes editor. The validator
 * and normalizer are the write-path gate for the backend meeting-notes save
 * route, which normalizes a client-supplied document before validating it.
 *
 * Allowlist source of truth: the meeting editors instantiate
 * @tiptap/starter-kit 3.22.3 with `code: false, codeBlock: false,
 * horizontalRule: false`, plus the MeetingImage / FileAttachmentNode /
 * InlineFileAttachmentNode / MeetingMention extensions and SourceRefExtension.
 * Anything outside the allowlist below is illegal in a meeting-note document.
 *
 * Write path contract (once wired): `normalizeMeetingNoteDocument` runs FIRST
 * (repairs what it can, drops what it cannot), then `validateMeetingNoteDocument`
 * (rejects the rest). Never validate a raw client document before normalizing it.
 */
import { z } from 'zod';
import { MentionEntityType, createMentionToken, parseMentionTokens } from './user-mention.js';
import { SpeakerSide } from './meeting-analysis.js';
// =============================================================================
// Limits
// =============================================================================
export const MEETING_NOTE_DOCUMENT_LIMITS = {
    /** Maximum node nesting depth; the doc root counts as depth 1. */
    maxDepth: 12,
    /** Maximum total node count in a document (root included). */
    maxNodes: 5000,
    /** Maximum serialized document size, in bytes. */
    maxBytes: 1_000_000,
    /** Maximum summed character count across every text node. */
    maxTextLength: 20_000,
};
// =============================================================================
// Allowlist
// =============================================================================
/**
 * Node types legal inside a meeting-note document. `doc` is the document root
 * (validated by `MeetingNoteDocumentSchema`) and is therefore not listed here.
 */
const ALLOWED_NODE_TYPES = [
    'paragraph',
    'heading',
    'blockquote',
    'bulletList',
    'orderedList',
    'listItem',
    'hardBreak',
    'image',
    'fileAttachment',
    'text',
    'mention',
    'inlineFileAttachment',
];
const ALLOWED_NODE_TYPE_SET = new Set(ALLOWED_NODE_TYPES);
/** Node types that never carry `content`. */
const LEAF_NODE_TYPES = new Set([
    'text',
    'mention',
    'hardBreak',
    'image',
    'fileAttachment',
    'inlineFileAttachment',
]);
/** Marks the meeting editors can emit. `code` is disabled, so it is illegal. */
const ALLOWED_MARK_TYPES = new Set([
    'bold',
    'italic',
    'strike',
    'underline',
    'link',
]);
/** Node types that carry the SourceRefExtension global attributes. */
const SOURCE_ATTR_NODE_TYPES = new Set(['paragraph', 'heading', 'listItem']);
/**
 * Link href schemes allowed by the schema. Deliberately excludes javascript:,
 * data:, vbscript: and every other scheme.
 *
 * Relative ("/notes") and protocol-relative ("//host/path") hrefs are also
 * rejected: meeting notes are synced across clients and rendered in a desktop
 * shell with no meaningful document base URL, so an absolute allowlisted URL is
 * the only href we can resolve safely.
 */
const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);
function isAllowedLinkHref(href) {
    try {
        return ALLOWED_LINK_PROTOCOLS.has(new URL(href).protocol);
    }
    catch {
        return false;
    }
}
// =============================================================================
// Attribute schemas
// =============================================================================
const sourceRefSchema = z
    .object({
    startTimeMs: z.number().int().min(0),
    endTimeMs: z.number().int().min(0),
    // The editor persists empty snippets/speakers for user-authored bullets
    // (`EMPTY_SOURCE_REF`), so these are intentionally non-empty-string lenient.
    transcriptSnippet: z.string(),
    speaker: z.string(),
    speakerSide: z.enum(Object.values(SpeakerSide)).optional(),
})
    .strict();
/** Global SourceRefExtension attributes, shared by paragraph / heading / listItem. */
const SOURCE_ATTR_SHAPE = {
    sourceRef: sourceRefSchema.nullable().optional(),
    isFromDraft: z.boolean().nullable().optional(),
    pointId: z.string().nullable().optional(),
    sectionId: z.string().nullable().optional(),
    userEdited: z.boolean().optional(),
};
const sourceAttrsSchema = z.object(SOURCE_ATTR_SHAPE).strict();
const headingAttrsSchema = z
    .object({
    // Meeting notes only ever use level 1 headings.
    level: z.literal(1),
    ...SOURCE_ATTR_SHAPE,
})
    .strict();
const orderedListAttrsSchema = z
    .object({
    start: z.number().int().nullable().optional(),
})
    .strict();
const imageAttrsSchema = z
    .object({
    src: z.string(),
    alt: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    width: z.number().nullable().optional(),
    height: z.number().nullable().optional(),
    imageId: z.string().nullable().optional(),
})
    .strict();
const attachmentAttrsSchema = z
    .object({
    attachmentId: z.string(),
    filename: z.string(),
    fileType: z.string(),
    sizeBytes: z.number(),
    downloadUrl: z.string(),
})
    .strict();
const mentionAttrsSchema = z
    .object({
    id: z.string().min(1),
    label: z.string().min(1),
    entityType: z.enum(Object.values(MentionEntityType)),
})
    .strict();
const linkAttrsSchema = z
    .object({
    href: z.string().refine(isAllowedLinkHref, {
        message: 'Link href must be an absolute http, https or mailto URL',
    }),
    target: z.string().nullable().optional(),
    rel: z.string().nullable().optional(),
})
    .strict();
const markSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('bold') }).strict(),
    z.object({ type: z.literal('italic') }).strict(),
    z.object({ type: z.literal('strike') }).strict(),
    z.object({ type: z.literal('underline') }).strict(),
    z.object({ type: z.literal('link'), attrs: linkAttrsSchema }).strict(),
]);
// =============================================================================
// Node + document schema
// =============================================================================
/**
 * A validated meeting-note node. Annotated with `TiptapNode` instead of being
 * inferred because the schema is recursive: every container's `content`
 * refers back to this schema.
 */
const meetingNoteNodeSchema = z.lazy(() => z.discriminatedUnion('type', [
    // ----- Inline nodes -----
    z
        .object({
        type: z.literal('text'),
        text: z.string(),
        marks: z.array(markSchema).optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('mention'),
        attrs: mentionAttrsSchema,
    })
        .strict(),
    z.object({ type: z.literal('hardBreak') }).strict(),
    z
        .object({
        type: z.literal('inlineFileAttachment'),
        attrs: attachmentAttrsSchema,
    })
        .strict(),
    // ----- Block nodes -----
    z
        .object({
        type: z.literal('paragraph'),
        attrs: sourceAttrsSchema.optional(),
        content: z.array(meetingNoteNodeSchema).optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('heading'),
        attrs: headingAttrsSchema,
        content: z.array(meetingNoteNodeSchema).optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('blockquote'),
        content: z.array(meetingNoteNodeSchema).optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('bulletList'),
        content: z.array(meetingNoteNodeSchema).optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('orderedList'),
        attrs: orderedListAttrsSchema.optional(),
        content: z.array(meetingNoteNodeSchema).optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('listItem'),
        attrs: sourceAttrsSchema.optional(),
        content: z.array(meetingNoteNodeSchema).optional(),
    })
        .strict(),
    // `image` is a block node in the draft editor and an inline node in the
    // enhanced editor, so its parent is deliberately unconstrained.
    z
        .object({
        type: z.literal('image'),
        attrs: imageAttrsSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal('fileAttachment'),
        attrs: attachmentAttrsSchema,
    })
        .strict(),
]));
function collectStats(node, depth, stats) {
    stats.nodes += 1;
    if (depth > stats.depth)
        stats.depth = depth;
    if (typeof node.text === 'string')
        stats.textLength += node.text.length;
    if (node.type === 'mention' && typeof node.attrs?.label === 'string') {
        stats.textLength += node.attrs.label.length;
    }
    for (const child of node.content ?? [])
        collectStats(child, depth + 1, stats);
}
/**
 * The strict meeting-note document schema: root `doc`, allowlisted nodes and
 * marks, per-type attr whitelists, and the `MEETING_NOTE_DOCUMENT_LIMITS` caps.
 */
export const MeetingNoteDocumentSchema = z
    .object({
    type: z.literal('doc'),
    content: z.array(meetingNoteNodeSchema),
})
    .strict()
    .superRefine((document, ctx) => {
    const stats = { nodes: 0, depth: 0, textLength: 0 };
    collectStats(document, 1, stats);
    if (stats.nodes > MEETING_NOTE_DOCUMENT_LIMITS.maxNodes) {
        ctx.addIssue(`Document has ${stats.nodes} nodes, which exceeds the limit of ${MEETING_NOTE_DOCUMENT_LIMITS.maxNodes}`);
    }
    if (stats.depth > MEETING_NOTE_DOCUMENT_LIMITS.maxDepth) {
        ctx.addIssue(`Document is ${stats.depth} levels deep, which exceeds the limit of ${MEETING_NOTE_DOCUMENT_LIMITS.maxDepth}`);
    }
    if (stats.textLength > MEETING_NOTE_DOCUMENT_LIMITS.maxTextLength) {
        ctx.addIssue(`Document contains ${stats.textLength} characters of text, which exceeds the limit of ${MEETING_NOTE_DOCUMENT_LIMITS.maxTextLength}`);
    }
    const bytes = new TextEncoder().encode(JSON.stringify(document)).length;
    if (bytes > MEETING_NOTE_DOCUMENT_LIMITS.maxBytes) {
        ctx.addIssue(`Document is ${bytes} bytes, which exceeds the limit of ${MEETING_NOTE_DOCUMENT_LIMITS.maxBytes}`);
    }
});
/**
 * Validate a meeting-note document against the allowlist and the size caps.
 * Throws a descriptive `Error` when the document is not legal.
 */
export function validateMeetingNoteDocument(input) {
    const result = MeetingNoteDocumentSchema.safeParse(input);
    if (!result.success) {
        const issues = result.error.issues
            .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
            .join('; ');
        throw new Error(`Invalid meeting note document: ${issues}`);
    }
    return result.data;
}
// =============================================================================
// Normalizer
// =============================================================================
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * Canonicalize a persisted sourceRef, folding the legacy `startMs` / `endMs`
 * keys onto `startTimeMs` / `endTimeMs`. Returns null when the value carries no
 * source-ref object at all (a null attr is dropped rather than kept).
 */
function normalizeTimestampMs(raw) {
    if (typeof raw !== 'number' || !Number.isFinite(raw))
        return 0;
    return Math.max(0, Math.round(raw));
}
export function normalizeTranscriptSourceRef(raw) {
    if (!isRecord(raw))
        return null;
    const startTimeMs = normalizeTimestampMs(typeof raw.startTimeMs === 'number' ? raw.startTimeMs : raw.startMs);
    const endTimeMs = normalizeTimestampMs(typeof raw.endTimeMs === 'number' ? raw.endTimeMs : raw.endMs);
    const ref = {
        startTimeMs,
        endTimeMs,
        transcriptSnippet: typeof raw.transcriptSnippet === 'string' ? raw.transcriptSnippet : '',
        speaker: typeof raw.speaker === 'string' ? raw.speaker : '',
    };
    const speakerSide = raw.speakerSide;
    if (speakerSide === 'sales' || speakerSide === 'client' || speakerSide === 'unknown') {
        ref.speakerSide = speakerSide;
    }
    return ref;
}
/**
 * Keep only allowlisted marks. An unrepairable symbol - a `code` mark, or a
 * link whose href is missing or uses a non-allowlisted scheme - is dropped
 * rather than passed through, because the validator cannot repair either one.
 */
function normalizeMarks(raw) {
    if (!Array.isArray(raw))
        return [];
    const marks = [];
    for (const mark of raw) {
        if (!isRecord(mark) || typeof mark.type !== 'string')
            continue;
        if (!ALLOWED_MARK_TYPES.has(mark.type))
            continue;
        if (mark.type === 'link') {
            const rawAttrs = isRecord(mark.attrs) ? mark.attrs : {};
            const href = typeof rawAttrs.href === 'string' ? rawAttrs.href : '';
            if (!isAllowedLinkHref(href))
                continue;
            const attrs = { href };
            if (typeof rawAttrs.target === 'string')
                attrs.target = rawAttrs.target;
            if (typeof rawAttrs.rel === 'string')
                attrs.rel = rawAttrs.rel;
            marks.push({ type: 'link', attrs });
            continue;
        }
        marks.push({ type: mark.type });
    }
    return marks;
}
/**
 * Normalize a node's attrs against the per-type allowlist. Null and undefined
 * attrs are dropped (they are legal but meaningless), heading levels are
 * coerced to 1, and a repeated pointId is removed so the duplicate is treated
 * as a new point - mirroring the editor's `seenPointIds` pass.
 */
function normalizeAttrs(type, raw, seenPointIds) {
    const source = isRecord(raw) ? raw : {};
    const attrs = {};
    if (type === 'heading')
        attrs.level = 1;
    if (type === 'orderedList' && typeof source.start === 'number' && Number.isInteger(source.start)) {
        attrs.start = source.start;
    }
    if (type === 'image') {
        if (typeof source.src === 'string')
            attrs.src = source.src;
        if (typeof source.alt === 'string')
            attrs.alt = source.alt;
        if (typeof source.title === 'string')
            attrs.title = source.title;
        if (typeof source.width === 'number')
            attrs.width = source.width;
        if (typeof source.height === 'number')
            attrs.height = source.height;
        if (typeof source.imageId === 'string')
            attrs.imageId = source.imageId;
    }
    else if (type === 'fileAttachment' || type === 'inlineFileAttachment') {
        // `uploading` is transient client upload state that the extension
        // serializes but never persists meaningfully.
        if (typeof source.attachmentId === 'string')
            attrs.attachmentId = source.attachmentId;
        if (typeof source.filename === 'string')
            attrs.filename = source.filename;
        if (typeof source.fileType === 'string')
            attrs.fileType = source.fileType;
        if (typeof source.sizeBytes === 'number')
            attrs.sizeBytes = source.sizeBytes;
        if (typeof source.downloadUrl === 'string')
            attrs.downloadUrl = source.downloadUrl;
    }
    else if (type === 'mention') {
        if (typeof source.id === 'string' && source.id.length > 0) {
            attrs.id = source.id;
            attrs.label = typeof source.label === 'string' && source.label.trim()
                ? source.label.trim()
                : source.id;
            attrs.entityType = source.entityType === 'contact' ? 'contact' : 'user';
        }
    }
    if (SOURCE_ATTR_NODE_TYPES.has(type)) {
        const sourceRef = normalizeTranscriptSourceRef(source.sourceRef);
        if (sourceRef)
            attrs.sourceRef = sourceRef;
        if (typeof source.isFromDraft === 'boolean')
            attrs.isFromDraft = source.isFromDraft;
        const pointId = source.pointId;
        if (typeof pointId === 'string' && pointId.length > 0) {
            if (!seenPointIds.has(pointId)) {
                attrs.pointId = pointId;
                seenPointIds.add(pointId);
            }
            // A duplicate pointId is intentionally left unset so the node is treated
            // as a brand new point by the reader.
        }
        if (typeof source.sectionId === 'string')
            attrs.sectionId = source.sectionId;
        if (source.userEdited === true)
            attrs.userEdited = true;
    }
    return Object.keys(attrs).length > 0 ? attrs : null;
}
function normalizeNode(node, seenPointIds) {
    if (!isRecord(node))
        return null;
    const type = typeof node.type === 'string' ? node.type : '';
    if (!ALLOWED_NODE_TYPE_SET.has(type))
        return null;
    if (type === 'text') {
        if (typeof node.text !== 'string')
            return null;
        const marks = normalizeMarks(node.marks);
        const mentionTokens = parseMentionTokens(node.text);
        if (mentionTokens.length === 0) {
            return marks.length > 0 ? { type, text: node.text, marks } : { type, text: node.text };
        }
        const normalized = [];
        let cursor = 0;
        for (const token of mentionTokens) {
            if (token.start > cursor) {
                normalized.push({
                    type,
                    text: node.text.slice(cursor, token.start),
                    ...(marks.length > 0 ? { marks } : {}),
                });
            }
            normalized.push({
                type: 'mention',
                attrs: {
                    id: token.entityId,
                    label: token.label,
                    entityType: token.entityType,
                },
            });
            cursor = token.end;
        }
        if (cursor < node.text.length) {
            normalized.push({
                type,
                text: node.text.slice(cursor),
                ...(marks.length > 0 ? { marks } : {}),
            });
        }
        return normalized;
    }
    const attrs = normalizeAttrs(type, node.attrs, seenPointIds);
    if (LEAF_NODE_TYPES.has(type)) {
        return attrs ? { type, attrs } : { type };
    }
    const content = Array.isArray(node.content) ? normalizeNodes(node.content, seenPointIds) : [];
    return {
        type,
        ...(attrs ? { attrs } : {}),
        ...(Array.isArray(node.content) ? { content } : {}),
    };
}
function normalizeNodes(nodes, seenPointIds) {
    const normalized = [];
    for (const node of nodes) {
        const result = normalizeNode(node, seenPointIds);
        if (Array.isArray(result))
            normalized.push(...result);
        else if (result)
            normalized.push(result);
    }
    return normalized;
}
/**
 * Coerce a plausible-but-untidy document into the canonical shape. Total: it
 * repairs what it can and drops what it cannot instead of throwing. Run this
 * before `validateMeetingNoteDocument` on every write path.
 */
export function normalizeMeetingNoteDocument(input) {
    const root = isRecord(input) ? input : {};
    const seenPointIds = new Set();
    const content = Array.isArray(root.content) ? normalizeNodes(root.content, seenPointIds) : [];
    return { type: 'doc', content };
}
// =============================================================================
// Transcript source helpers
// =============================================================================
const EMPTY_SOURCE_REF = {
    startTimeMs: 0,
    endTimeMs: 0,
    transcriptSnippet: '',
    speaker: '',
};
/**
 * Canonical transcript-source predicate: whether a sourceRef carries any real
 * transcript signal. Zeroed aliases (`startMs` / `endMs`) are not consulted
 * here - `normalizeMeetingNoteDocument` canonicalizes them first.
 */
export function hasTranscriptSourceRef(value) {
    if (!value || typeof value !== 'object')
        return false;
    const ref = value;
    const start = typeof ref.startTimeMs === 'number' ? ref.startTimeMs : 0;
    const end = typeof ref.endTimeMs === 'number' ? ref.endTimeMs : 0;
    return (end > start ||
        (typeof ref.transcriptSnippet === 'string' && ref.transcriptSnippet.trim().length > 0) ||
        (typeof ref.speaker === 'string' && ref.speaker.trim().length > 0));
}
/**
 * Parse inline markdown (**bold** and *italic*) into Tiptap text nodes with marks.
 * Returns an array of nodes ready for use in a paragraph.
 */
function parseMarkdownToTiptapNodes(text) {
    const nodes = [];
    let remaining = text;
    // Bold first (**...**), then italic (*...*)
    const boldPattern = /\*\*(.+?)\*\*/;
    const italicPattern = /\*([^*]+)\*/;
    while (remaining.length > 0) {
        const mention = parseMentionTokens(remaining)[0];
        const boldMatch = boldPattern.exec(remaining);
        const italicMatch = italicPattern.exec(remaining);
        if (mention && (!boldMatch || mention.start <= boldMatch.index) && (!italicMatch || mention.start <= italicMatch.index)) {
            if (mention.start > 0) {
                nodes.push(...parseMarkdownToTiptapNodes(remaining.substring(0, mention.start)));
            }
            nodes.push({
                type: 'mention',
                attrs: {
                    id: mention.entityId,
                    label: mention.label,
                    entityType: mention.entityType,
                },
            });
            remaining = remaining.substring(mention.end);
            continue;
        }
        let earliestMatch = null;
        let matchType = null;
        if (boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index)) {
            earliestMatch = boldMatch;
            matchType = 'bold';
        }
        else if (italicMatch) {
            earliestMatch = italicMatch;
            matchType = 'italic';
        }
        if (earliestMatch && matchType) {
            // Text before the match
            if (earliestMatch.index > 0) {
                nodes.push({ type: 'text', text: remaining.substring(0, earliestMatch.index) });
            }
            // Parse the formatted content recursively so mention tokens inside
            // markdown marks still become first-class mention nodes.
            const markType = matchType === 'bold' ? 'bold' : 'italic';
            const formattedNodes = parseMarkdownToTiptapNodes(earliestMatch[1]);
            for (const node of formattedNodes) {
                if (node.type !== 'text') {
                    nodes.push(node);
                    continue;
                }
                nodes.push({
                    ...node,
                    marks: [...(node.marks ?? []), { type: markType }],
                });
            }
            remaining = remaining.substring(earliestMatch.index + earliestMatch[0].length);
        }
        else {
            // No more matches
            if (remaining) {
                nodes.push({ type: 'text', text: remaining });
            }
            break;
        }
    }
    return nodes.length > 0 ? nodes : [];
}
/**
 * Build the ordered inline node sequence for a point's `contentParts`.
 * - text parts parse inline markdown (bold/italic still work)
 * - file parts become an `inlineFileAttachment` node (metadata resolved from the store's attachments)
 * - image parts become an inline `image` node (src/imageId resolved from the store's images)
 */
function buildContentNodesFromParts(parts, attachments, images) {
    const nodes = [];
    for (const part of parts) {
        if (part.type === 'text') {
            nodes.push(...parseMarkdownToTiptapNodes(part.text));
        }
        else if (part.type === 'file') {
            const attachment = attachments.find((a) => a.id === part.attachmentId);
            nodes.push({
                type: 'inlineFileAttachment',
                attrs: {
                    attachmentId: part.attachmentId,
                    filename: attachment?.filename ?? 'Attachment',
                    fileType: attachment?.fileType ?? '',
                    sizeBytes: attachment?.sizeBytes ?? 0,
                    downloadUrl: attachment?.downloadUrl ?? '',
                },
            });
        }
        else if (part.type === 'image') {
            const image = images.find((i) => i.id === part.attachmentId);
            nodes.push({
                type: 'image',
                attrs: {
                    src: image?.url ?? '',
                    imageId: image?.id ?? part.attachmentId,
                },
            });
        }
    }
    return nodes;
}
/**
 * Build the paragraph content for a single point. Prefers `contentParts` when
 * present; otherwise falls back to the legacy whole-point-image / flat-text paths.
 */
function buildParagraphNodes(point, attachments, images) {
    if (point.contentParts && point.contentParts.length > 0) {
        return buildContentNodesFromParts(point.contentParts, attachments, images);
    }
    if (point.type === 'image' && point.imageUrl) {
        // Legacy whole-point image (predates contentParts) - render as a lone image.
        return [{ type: 'image', attrs: { src: point.imageUrl } }];
    }
    return point.text ? parseMarkdownToTiptapNodes(point.text) : [];
}
/**
 * Convert an array of EnhancedSection objects into a Tiptap document.
 *
 * Structure:
 *   doc
 *     heading (level 1) - section header, attrs: { sectionId }
 *     bulletList
 *       listItem - attrs: { sourceRef, isFromDraft, pointId, sectionId }
 *         paragraph
 *           [inline text | inlineFileAttachment | image]  (ordered contentParts)
 *       ...
 *     heading (level 1) - next section
 *     bulletList
 *       ...
 *
 * `attachments` / `images` are the store's file/image metadata used to resolve a
 * part's `attachmentId` to displayable attributes (filename, url, etc.).
 */
export function sectionsToEnhancedDocument(sections, attachments = [], images = []) {
    const content = [];
    for (const section of sections) {
        // Section heading
        content.push({
            type: 'heading',
            attrs: {
                level: 1,
                sectionId: section.id,
                // Older summaries do not have the section-level flag. Infer it from
                // point provenance until the normalized store value is available.
                isFromDraft: section.isFromDraft ?? section.points.some((point) => point.isFromDraft === true),
            },
            content: section.header ? parseMarkdownToTiptapNodes(section.header) : [],
        });
        // Build bullet lists from points, supporting nested indentation. A user may
        // also type in the trailing document paragraph; preserve that node as plain
        // text instead of forcing it into a bullet during autosave.
        if (section.points.length > 0) {
            const topLevelItems = [];
            let lastTopLevelItem = null;
            const flushBulletList = () => {
                if (topLevelItems.length === 0)
                    return;
                content.push({
                    type: 'bulletList',
                    content: topLevelItems.splice(0),
                });
                lastTopLevelItem = null;
            };
            for (const point of section.points) {
                if (point.isStandalone) {
                    flushBulletList();
                    content.push({
                        type: 'paragraph',
                        attrs: {
                            sourceRef: normalizeTranscriptSourceRef(point.sourceRef),
                            isFromDraft: point.isFromDraft ?? true,
                            sectionId: section.id,
                            ...(point.userEdited === true ? { userEdited: true } : {}),
                        },
                        content: buildParagraphNodes(point, attachments, images),
                    });
                    continue;
                }
                const itemAttrs = {
                    sourceRef: normalizeTranscriptSourceRef(point.sourceRef),
                    isFromDraft: point.isFromDraft ?? false,
                    pointId: point.id,
                    sectionId: section.id,
                    ...(point.userEdited === true ? { userEdited: true } : {}),
                };
                const listItem = {
                    type: 'listItem',
                    attrs: itemAttrs,
                    content: [
                        {
                            type: 'paragraph',
                            content: buildParagraphNodes(point, attachments, images),
                        },
                    ],
                };
                if (point.indent === 1 && lastTopLevelItem) {
                    // Nest under the last top-level item
                    const existingNestedList = lastTopLevelItem.content?.find((child) => child.type === 'bulletList');
                    if (existingNestedList) {
                        // Append to existing nested bulletList
                        existingNestedList.content.push(listItem);
                    }
                    else {
                        // Create a new nested bulletList inside the parent listItem
                        lastTopLevelItem.content.push({
                            type: 'bulletList',
                            content: [listItem],
                        });
                    }
                }
                else {
                    // Top-level item (indent=0, or indent=1 with no parent)
                    topLevelItems.push(listItem);
                    lastTopLevelItem = listItem;
                }
            }
            flushBulletList();
        }
    }
    return { type: 'doc', content };
}
/**
 * Extract the plain text content from a Tiptap node, recursively concatenating
 * all text nodes.
 */
function extractText(node) {
    if (node.type === 'mention') {
        const id = typeof node.attrs?.id === 'string' ? node.attrs.id : '';
        const label = typeof node.attrs?.label === 'string' ? node.attrs.label : id;
        const entityType = node.attrs?.entityType === 'contact' ? 'contact' : 'user';
        return id ? createMentionToken(entityType, id, label) : label;
    }
    if (node.text)
        return node.text;
    if (!node.content)
        return '';
    return node.content.map(extractText).join('');
}
/** Concatenate the plain text of the text parts in a reconstructed contentParts array. */
function textFromParts(parts) {
    return parts
        .filter((p) => p.type === 'text')
        .map((p) => p.text)
        .join('');
}
/**
 * Walk a listItem's paragraph content in order and reconstruct the ordered
 * `contentParts`. Adjacent text nodes are merged back into a single text part;
 * `inlineFileAttachment` / `image` nodes become file / image parts. An image
 * node with no `imageId` is a legacy whole-point image and is NOT emitted here.
 */
function buildContentPartsFromNodes(nodes) {
    const parts = [];
    let textBuffer = '';
    const flushText = () => {
        if (textBuffer) {
            parts.push({ type: 'text', text: textBuffer });
            textBuffer = '';
        }
    };
    for (const node of nodes) {
        if (node.type === 'text' || node.type === 'mention') {
            const text = extractText(node);
            if (text)
                textBuffer += text;
        }
        else if (node.type === 'inlineFileAttachment') {
            flushText();
            const attachmentId = node.attrs?.attachmentId;
            if (attachmentId)
                parts.push({ type: 'file', attachmentId });
        }
        else if (node.type === 'image') {
            flushText();
            const imageId = node.attrs?.imageId;
            if (imageId)
                parts.push({ type: 'image', attachmentId: imageId });
        }
    }
    flushText();
    return parts;
}
/**
 * Convert a single listItem node back to an EnhancedSummaryPoint.
 *
 * Round-tripping rules:
 * - If the paragraph contains a file/image part (contentParts with an embed),
 *   reconstruct `contentParts` so user edits do not lose chips.
 * - If the paragraph has a legacy whole-point image (src only, no imageId),
 *   keep the legacy `type: 'image'` / `imageUrl` shape.
 * - Otherwise collapse to the flat `text` (no contentParts) so legacy notes
 *   stay stable.
 */
function enhancedPointFromListItem(listItem, indent, forcePlain = false) {
    const attrs = listItem.attrs ?? {};
    const nonListContent = listItem.content?.filter((child) => child.type !== 'bulletList') ?? [];
    const paragraphNodes = nonListContent.flatMap((child) => child.content ?? []);
    const id = forcePlain ? crypto.randomUUID() : attrs.pointId || crypto.randomUUID();
    const sourceRef = forcePlain
        ? EMPTY_SOURCE_REF
        : (normalizeTranscriptSourceRef(attrs.sourceRef) ?? EMPTY_SOURCE_REF);
    // A newly typed bullet is user-authored, but has no transcript source. Keep
    // it visually distinct from transcript-only AI points while remaining a
    // plain editable bullet (the source button still stays hidden).
    const isFromDraft = forcePlain || !hasTranscriptSourceRef(sourceRef)
        ? true
        : (attrs.isFromDraft ?? false);
    const parts = buildContentPartsFromNodes(paragraphNodes);
    const hasEmbed = parts.some((p) => p.type !== 'text');
    const legacyImageNode = paragraphNodes.find((node) => node.type === 'image' && !node.attrs?.imageId);
    if (hasEmbed) {
        return {
            id,
            text: textFromParts(parts),
            indent,
            sourceRef,
            isFromDraft,
            contentParts: parts,
            ...(attrs.userEdited === true ? { userEdited: true } : {}),
        };
    }
    if (legacyImageNode) {
        return {
            id,
            text: '',
            indent,
            sourceRef,
            isFromDraft,
            type: 'image',
            imageUrl: legacyImageNode.attrs?.src ?? '',
            ...(attrs.userEdited === true ? { userEdited: true } : {}),
        };
    }
    return {
        id,
        text: paragraphNodes.map(extractText).join(''),
        indent,
        sourceRef,
        isFromDraft,
        ...(attrs.userEdited === true ? { userEdited: true } : {}),
    };
}
/** Append a bulletList node's list items to a section, preserving nesting and pointId dedup. */
function appendBulletListPoints(section, bulletList, seenPointIds) {
    for (const listItem of bulletList.content ?? []) {
        if (listItem.type !== 'listItem')
            continue;
        const pointId = listItem.attrs?.pointId;
        const forcePlain = !!pointId && seenPointIds.has(pointId);
        if (pointId)
            seenPointIds.add(pointId);
        section.points.push(enhancedPointFromListItem(listItem, 0, forcePlain));
        // Process nested bulletList children as indent: 1
        const nestedBulletList = listItem.content?.find((child) => child.type === 'bulletList');
        if (!nestedBulletList)
            continue;
        for (const nestedItem of nestedBulletList.content ?? []) {
            if (nestedItem.type !== 'listItem')
                continue;
            const nestedPointId = nestedItem.attrs?.pointId;
            const forcePlainNested = !!nestedPointId && seenPointIds.has(nestedPointId);
            if (nestedPointId)
                seenPointIds.add(nestedPointId);
            section.points.push(enhancedPointFromListItem(nestedItem, 1, forcePlainNested));
        }
    }
}
/**
 * Convert a Tiptap document (from `editor.getJSON()`) back to EnhancedSection[].
 *
 * Walks top-level nodes:
 * - heading → starts a new section
 * - bulletList → list items become points in the current section
 * - paragraph → becomes a standalone point in the current section
 */
export function documentToEnhancedSections(document) {
    const sections = [];
    let currentSection = null;
    const seenPointIds = new Set();
    for (const node of document.content ?? []) {
        if (node.type === 'heading') {
            // Start a new section
            currentSection = {
                id: node.attrs?.sectionId || crypto.randomUUID(),
                header: extractText(node),
                isFromDraft: node.attrs?.isFromDraft,
                points: [],
            };
            sections.push(currentSection);
        }
        else if (node.type === 'bulletList') {
            // Orphan bullet list (no section header above it) - create the default section
            if (!currentSection) {
                currentSection = {
                    id: crypto.randomUUID(),
                    header: 'Notes',
                    points: [],
                };
                sections.push(currentSection);
            }
            appendBulletListPoints(currentSection, node, seenPointIds);
        }
        else if (node.type === 'paragraph') {
            // Handle standalone paragraphs - convert to points in the current section
            const text = extractText(node).trim();
            if (!text)
                continue; // Skip empty paragraphs
            // If no section exists yet, create a default one
            if (!currentSection) {
                currentSection = {
                    id: crypto.randomUUID(),
                    header: 'Notes',
                    points: [],
                };
                sections.push(currentSection);
            }
            // Add paragraph text as a point in the current section
            currentSection.points.push({
                id: crypto.randomUUID(),
                text,
                indent: 0,
                sourceRef: normalizeTranscriptSourceRef(node.attrs?.sourceRef) ?? EMPTY_SOURCE_REF,
                isFromDraft: node.attrs?.isFromDraft ?? true,
                isStandalone: true,
            });
        }
    }
    return sections;
}
function cloneJsonValue(value) {
    if (Array.isArray(value))
        return value.map(cloneJsonValue);
    if (isRecord(value))
        return cloneRecord(value);
    return value;
}
function cloneRecord(value) {
    const result = {};
    for (const [key, item] of Object.entries(value))
        result[key] = cloneJsonValue(item);
    return result;
}
function cloneNode(node) {
    return {
        type: node.type,
        ...(node.attrs ? { attrs: cloneRecord(node.attrs) } : {}),
        ...(node.content ? { content: node.content.map(cloneNode) } : {}),
        ...(node.marks
            ? {
                marks: node.marks.map((mark) => ({
                    type: mark.type,
                    ...(mark.attrs ? { attrs: cloneRecord(mark.attrs) } : {}),
                })),
            }
            : {}),
        ...(node.text !== undefined ? { text: node.text } : {}),
    };
}
function canonicalJson(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    if (isRecord(value)) {
        return `{${Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
            .join(',')}}`;
    }
    return JSON.stringify(value) ?? 'null';
}
function normalizedPointContent(node) {
    const normalize = (current) => {
        const attrs = current.attrs
            ? Object.fromEntries(Object.entries(current.attrs).filter(([key]) => !['pointId', 'sectionId', 'userEdited', 'isFromDraft'].includes(key)))
            : undefined;
        return {
            type: current.type,
            ...(attrs && Object.keys(attrs).length > 0 ? { attrs } : {}),
            ...(current.text !== undefined
                ? { text: current.text.replace(/\s+/g, ' ').trim().toLowerCase() }
                : {}),
            ...(current.marks ? { marks: current.marks } : {}),
            ...(current.content ? { content: current.content.map(normalize) } : {}),
        };
    };
    const key = canonicalJson(normalize(node));
    return key.includes('"text":""') && !node.content?.some((child) => child.type !== 'text')
        ? null
        : key;
}
function sourceRefIdentity(node) {
    const sourceRef = normalizeTranscriptSourceRef(node.attrs?.sourceRef);
    if (!sourceRef)
        return null;
    const { startTimeMs, endTimeMs } = sourceRef;
    const transcriptSnippet = sourceRef.transcriptSnippet.replace(/\s+/g, ' ').trim().toLowerCase();
    const speaker = sourceRef.speaker.trim().toLowerCase();
    const speakerSide = sourceRef.speakerSide ?? '';
    if (endTimeMs <= startTimeMs && !transcriptSnippet && !speaker)
        return null;
    return canonicalJson({ startTimeMs, endTimeMs, transcriptSnippet, speaker, speakerSide });
}
function pointIdentity(node) {
    const pointId = typeof node.attrs?.pointId === 'string' && node.attrs.pointId.length > 0
        ? node.attrs.pointId
        : null;
    return {
        pointId,
        sourceRef: sourceRefIdentity(node),
        content: normalizedPointContent(node),
    };
}
function pointsMatch(left, right) {
    if (left.pointId && right.pointId && left.pointId === right.pointId)
        return true;
    if (left.sourceRef && right.sourceRef && left.sourceRef === right.sourceRef)
        return true;
    return left.content !== null && right.content !== null && left.content === right.content;
}
function collectPointInfos(document) {
    const points = [];
    let order = 0;
    const visit = (node, sectionId, sectionHeader) => {
        const nodeSectionId = typeof node.attrs?.sectionId === 'string' ? node.attrs.sectionId : sectionId;
        if (node.type === 'listItem') {
            points.push({ node, sectionId: nodeSectionId, sectionHeader, order: order++ });
        }
        for (const child of node.content ?? [])
            visit(child, nodeSectionId, sectionHeader);
    };
    let sectionId = null;
    let sectionHeader = '';
    for (const node of document.content) {
        if (node.type === 'heading') {
            sectionId = typeof node.attrs?.sectionId === 'string' ? node.attrs.sectionId : null;
            sectionHeader = extractText(node).replace(/\s+/g, ' ').trim().toLowerCase();
        }
        visit(node, sectionId, sectionHeader);
    }
    return points;
}
function isProtectedPoint(info) {
    return info.node.attrs?.userEdited === true || pointIdentity(info.node).pointId === null;
}
function pointDeleted(node, deletedIds) {
    const identity = pointIdentity(node);
    const keys = [identity.pointId, identity.sourceRef, identity.content].filter((key) => key !== null);
    return keys.some((key) => deletedIds.has(key) || deletedIds.has(`point:${key}`));
}
function transformMergedNode(node, protectedPoints, usedPreviousOrders, deletedIds) {
    if (node.type === 'listItem') {
        if (pointDeleted(node, deletedIds))
            return null;
        const identity = pointIdentity(node);
        const previous = protectedPoints.find((candidate) => !usedPreviousOrders.has(candidate.order) && pointsMatch(identity, pointIdentity(candidate.node)));
        if (previous) {
            usedPreviousOrders.add(previous.order);
            return cloneNode(previous.node);
        }
    }
    if (!node.content)
        return cloneNode(node);
    const content = node.content
        .map((child) => transformMergedNode(child, protectedPoints, usedPreviousOrders, deletedIds))
        .filter((child) => child !== null);
    return { ...cloneNode(node), content };
}
function normalizedHeader(node) {
    return node.type === 'heading' ? extractText(node).replace(/\s+/g, ' ').trim().toLowerCase() : '';
}
function sectionMatches(node, point) {
    if (node.type !== 'heading')
        return false;
    const sectionId = typeof node.attrs?.sectionId === 'string' ? node.attrs.sectionId : null;
    if (point.sectionId && sectionId)
        return point.sectionId === sectionId;
    return point.sectionHeader.length > 0 && normalizedHeader(node) === point.sectionHeader;
}
function appendProtectedPointToSection(content, point) {
    let inSection = false;
    for (let index = 0; index < content.length; index += 1) {
        const node = content[index];
        if (node.type === 'heading') {
            inSection = sectionMatches(node, point);
            continue;
        }
        if (!inSection)
            continue;
        if (node.type === 'bulletList') {
            node.content = [...(node.content ?? []), cloneNode(point.node)];
            return true;
        }
        if (node.type === 'heading')
            break;
    }
    const headingIndex = content.findIndex((node) => sectionMatches(node, point));
    if (headingIndex < 0)
        return false;
    let insertionIndex = headingIndex + 1;
    while (insertionIndex < content.length && content[insertionIndex].type !== 'heading')
        insertionIndex += 1;
    content.splice(insertionIndex, 0, {
        type: 'bulletList',
        content: [cloneNode(point.node)],
    });
    return true;
}
function appendOrphanProtectedPoints(content, points) {
    const groups = new Map();
    for (const point of points) {
        const key = point.sectionId ?? point.sectionHeader ?? 'notes';
        const group = groups.get(key);
        if (group)
            group.push(point);
        else
            groups.set(key, [point]);
    }
    for (const group of groups.values()) {
        const first = group[0];
        content.push({
            type: 'heading',
            attrs: {
                level: 1,
                ...(first.sectionId ? { sectionId: first.sectionId } : {}),
            },
            content: [{ type: 'text', text: first.sectionHeader || 'Notes' }],
        });
        content.push({
            type: 'bulletList',
            content: group.map((point) => cloneNode(point.node)),
        });
    }
}
function nodeContentSignature(node) {
    const cloned = cloneNode(node);
    if (cloned.attrs)
        delete cloned.attrs.userEdited;
    return canonicalJson(cloned);
}
/**
 * Mark the submitted document's changed points as user-authored. This runs
 * after the client revision fence is checked, so an edit can never silently
 * replace a newer document. New bullets without a stable point id are always
 * protected, even when they happen to match generated text.
 */
export function markEnhancedDocumentUserEdits(previous, next) {
    const previousPoints = previous ? collectPointInfos(previous) : [];
    const usedPreviousOrders = new Set();
    const markNode = (node) => {
        if (node.type !== 'listItem') {
            return node.content
                ? { ...cloneNode(node), content: node.content.map(markNode) }
                : cloneNode(node);
        }
        const identity = pointIdentity(node);
        const previousPoint = previousPoints.find((candidate) => !usedPreviousOrders.has(candidate.order) && pointsMatch(identity, pointIdentity(candidate.node)));
        if (previousPoint)
            usedPreviousOrders.add(previousPoint.order);
        const userEdited = node.attrs?.userEdited === true ||
            identity.pointId === null ||
            (previousPoint !== undefined && nodeContentSignature(previousPoint.node) !== nodeContentSignature(node));
        const markedChildren = node.content?.map(markNode);
        const attrs = node.attrs ? cloneRecord(node.attrs) : {};
        if (userEdited)
            attrs.userEdited = true;
        else
            delete attrs.userEdited;
        return {
            ...cloneNode(node),
            ...(Object.keys(attrs).length > 0 ? { attrs } : {}),
            ...(markedChildren ? { content: markedChildren } : {}),
        };
    };
    return {
        type: 'doc',
        content: next.content.map(markNode),
    };
}
/**
 * Merge an AI-generated document over the last completed snapshot. Protected
 * user points are copied as their original TipTap nodes, preserving marks,
 * attachments, nested content, and custom attributes byte-for-byte in the
 * resulting JSON shape. Tombstones are checked before matching and protected
 * points that disappear from the generated response are reinserted into their
 * surviving section.
 */
export function mergeEnhancedDocument(previous, generated, deletedIds = []) {
    const previousPoints = previous ? collectPointInfos(previous) : [];
    const protectedPoints = previousPoints.filter((point) => isProtectedPoint(point) && !pointDeleted(point.node, new Set(deletedIds)));
    const usedPreviousOrders = new Set();
    const deletedIdSet = new Set(deletedIds);
    const content = generated.content
        .map((node) => transformMergedNode(node, protectedPoints, usedPreviousOrders, deletedIdSet))
        .filter((node) => node !== null);
    const missingPoints = protectedPoints.filter((point) => !usedPreviousOrders.has(point.order));
    const orphanPoints = [];
    for (const point of missingPoints) {
        if (!appendProtectedPointToSection(content, point))
            orphanPoints.push(point);
    }
    appendOrphanProtectedPoints(content, orphanPoints);
    return { type: 'doc', content };
}
// =============================================================================
// Draft document -> NoteSection projection
// =============================================================================
// The draft editor stores a free-form TipTap document with no node ids of its
// own, while the backend prompt builders, the embedding service and the email
// service all consume the lossy NoteSection[] store shape, whose `id` maps to a
// `UUID NOT NULL` column. `stableNoteId` bridges the two: it derives an id from
// the content so identical content projects to identical ids on every call
// (merge logic depends on that stability), without an id being persisted in the
// document itself.
/**
 * Serialize id parts length-first (as `<length>:<value>`) so `['a b']` and
 * `['a', 'b']` can never collide on the same hash, with no separator character
 * that a TipTap text node could legally contain.
 */
function serializeNoteIdParts(parts) {
    return parts.map((part) => `${String(part).length}:${String(part)}`).join('');
}
/** FNV-1a 64-bit prime (2^40 + 2^8 + 0xb3). */
const FNV1A_64_PRIME = 0x100000001b3n;
/**
 * The two independent FNV-1a 64-bit offset bases. The second is the
 * byte-swapped first, so the 128-bit id is two structurally independent 64-bit
 * passes rather than one 64-bit hash written twice.
 */
const FNV1A_64_OFFSET_A = 0xcbf29ce484222325n;
const FNV1A_64_OFFSET_B = 0x84222325cbf29ce4n;
/** Keeps each FNV-1a round in the unsigned 64-bit range. */
const FNV1A_64_MASK = 0xffffffffffffffffn;
/** RFC 4122 variant nibbles (the `10xx` high bits of the 4th group). */
const UUID_VARIANT_NIBBLES = '89ab';
/** The header of the implicit section that holds content with no heading above it. */
const IMPLICIT_SECTION_HEADER = 'Notes';
/** Lowercased `Notes`, matched case-insensitively by `documentSectionHeaders`. */
const IMPLICIT_SECTION_HEADER_KEY = IMPLICIT_SECTION_HEADER.toLowerCase();
function fnv1a64(bytes, offsetBasis) {
    let hash = offsetBasis;
    for (const byte of bytes) {
        hash = ((hash ^ BigInt(byte)) * FNV1A_64_PRIME) & FNV1A_64_MASK;
    }
    return hash;
}
/**
 * Deterministic, UUID-shaped id for a projected section or bullet.
 *
 * Draft documents carry no node ids, but `NoteSection.id` / `NoteBullet.id` map
 * to `UUID NOT NULL` columns and merge logic has to agree on an id for
 * identical content across calls. This derives one from the content instead of
 * minting a random uuid: the same `namespace` and `parts` always produce the
 * same id, on Node and in the browser alike, because it is a pure function of
 * its inputs with no `crypto` or `Math.random()` involved.
 *
 * Deliberately NOT cryptographic and not collision-resistant against an
 * adversary - it is two 64-bit FNV-1a passes sized to fill 128 bits, then
 * reshaped into RFC 4122 v4 form (version nibble `4`, variant nibble `8`-`b`)
 * so a strict `z.string().uuid()` accepts it.
 */
export function stableNoteId(namespace, ...parts) {
    const bytes = new TextEncoder().encode(serializeNoteIdParts([namespace, ...parts]));
    const hex = [
        fnv1a64(bytes, FNV1A_64_OFFSET_A).toString(16).padStart(16, '0'),
        fnv1a64(bytes, FNV1A_64_OFFSET_B).toString(16).padStart(16, '0'),
    ].join('');
    // 8-4-4-4-12: index 12 opens the 3rd group (version), index 16 the 4th (variant).
    const chars = hex.split('');
    chars[12] = '4';
    chars[16] = UUID_VARIANT_NIBBLES[Number.parseInt(chars[16], 16) & 0x3];
    const id = chars.join('');
    return `${id.slice(0, 8)}-${id.slice(8, 12)}-${id.slice(12, 16)}-${id.slice(16, 20)}-${id.slice(20)}`;
}
/** Start (and make current) a section. The current section is always the last one. */
function startNoteSection(projection, header) {
    const section = {
        id: stableNoteId(projection.idNamespace, 'section', projection.sections.length, header),
        header,
        bullets: [],
    };
    projection.sections.push(section);
    return section;
}
/**
 * Append a bullet to the current section, creating the implicit `Notes` section
 * first when the document has content but no heading above it.
 */
function appendNoteBullet(projection, text, indent) {
    const current = projection.sections[projection.sections.length - 1];
    const section = current ?? startNoteSection(projection, IMPLICIT_SECTION_HEADER);
    const bullet = {
        id: stableNoteId(projection.idNamespace, 'bullet', projection.sections.length - 1, section.bullets.length, text),
        text,
        indent,
    };
    section.bullets.push(bullet);
}
/** Project one list item's first paragraph, then any list nested inside it. */
function projectNoteList(list, indent, projection) {
    for (const listItem of list.content ?? []) {
        if (listItem.type !== 'listItem')
            continue;
        const children = listItem.content ?? [];
        const paragraph = children.find((child) => child.type === 'paragraph');
        if (paragraph) {
            const text = extractText(paragraph).trim();
            if (text)
                appendNoteBullet(projection, text, indent);
        }
        for (const child of children) {
            if (child.type === 'bulletList' || child.type === 'orderedList') {
                // NoteBullet only models one level of nesting, so deeper lists clamp to 1.
                projectNoteList(child, 1, projection);
            }
        }
    }
}
/**
 * Walk a node list in document order, appending bullets to the current section.
 *
 * `indent` is the indent the walked level projects at: 0 at the document root
 * and inside a blockquote, 1 inside a nested list.
 */
function projectNoteNodes(nodes, indent, projection) {
    for (const node of nodes) {
        if (node.type === 'heading') {
            const header = extractText(node).trim();
            if (header)
                startNoteSection(projection, header);
        }
        else if (node.type === 'paragraph') {
            const text = extractText(node).trim();
            if (text)
                appendNoteBullet(projection, text, indent);
        }
        else if (node.type === 'bulletList' || node.type === 'orderedList') {
            projectNoteList(node, indent, projection);
        }
        else if (node.type === 'blockquote') {
            projectNoteNodes(node.content ?? [], 0, projection);
        }
        // image / fileAttachment / inlineFileAttachment / hardBreak / text have no
        // NoteBullet slot: they stay in the document for the renderer and are
        // simply not projected.
    }
}
/**
 * Project a free-form draft document into the lossy `NoteSection[]` shape.
 *
 * Walk order and rules:
 * - a `heading` with non-empty trimmed text starts a new section
 * - a `bulletList` / `orderedList` projects each list item's first paragraph as
 *   a bullet; a list nested inside that item projects at `indent: 1`
 * - a top-level `paragraph` with non-empty text becomes a bullet
 * - a `blockquote` recurses into its children at `indent: 0`
 * - content with no heading above it lands in one implicit `Notes` section
 *
 * Two headings with the same text stay two sections - de-duplication is the
 * caller's job. Returns `[]` when nothing in the document is projectable.
 */
export function documentToNoteSections(document, opts) {
    const projection = { idNamespace: opts.idNamespace, sections: [] };
    projectNoteNodes(document.content, 0, projection);
    return projection.sections;
}
function nodeListHasText(nodes) {
    for (const node of nodes) {
        if (typeof node.text === 'string' && node.text.trim().length > 0)
            return true;
        if (node.type === 'mention' && typeof node.attrs?.label === 'string' && node.attrs.label.trim().length > 0) {
            return true;
        }
        if (nodeListHasText(node.content ?? []))
            return true;
    }
    return false;
}
/** Whether any text node in the document carries non-whitespace content. */
export function documentHasContent(document) {
    return nodeListHasText(document.content);
}
function nodeListHasHeading(nodes) {
    for (const node of nodes) {
        if (node.type === 'heading' && extractText(node).trim().length > 0)
            return true;
        if (nodeListHasHeading(node.content ?? []))
            return true;
    }
    return false;
}
/** Whether the document contains a heading with non-whitespace text. */
export function documentHasHeadings(document) {
    return nodeListHasHeading(document.content);
}
/**
 * The header skeleton used to constrain the enhancement prompt: the projected
 * section headers, minus the implicit `Notes` header, trimmed and
 * case-insensitively de-duplicated (first occurrence wins).
 */
export function documentSectionHeaders(document) {
    const headers = [];
    const seen = new Set();
    for (const section of documentToNoteSections(document, { idNamespace: '' })) {
        const header = section.header.trim();
        const key = header.toLowerCase();
        if (!header || key === IMPLICIT_SECTION_HEADER_KEY || seen.has(key))
            continue;
        seen.add(key);
        headers.push(header);
    }
    return headers;
}
